/** Cómo se dice cada cosa y cómo se enseña cada fecha.
 *
 * El backend ya manda `estado_texto` en los sitios donde lo necesita la lista; esto es para lo
 * que se pinta sin pasar por ahí, como los desplegables y las columnas del tablero.
 */
import type { EstadoProyecto, EstadoSolicitud, EstadoTarea, Papel, Prioridad } from './tipos'

export const ESTADO_SOLICITUD: Record<EstadoSolicitud, string> = {
  new: 'Nueva',
  reviewing: 'En revisión',
  needs_info: 'Falta información',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  planned: 'Planificada',
  in_development: 'En desarrollo',
  testing: 'En pruebas',
  closing: 'Pendiente de cierre',
  completed: 'Completada',
  cancelled: 'Cancelada',
}

export const ESTADO_TAREA: Record<EstadoTarea, string> = {
  backlog: 'Reserva',
  todo: 'Por hacer',
  in_progress: 'En curso',
  blocked: 'Bloqueada',
  review: 'En revisión',
  done: 'Hecha',
  cancelled: 'Cancelada',
}

export const ESTADO_PROYECTO: Record<EstadoProyecto, string> = {
  planning: 'En planificación',
  active: 'Activo',
  paused: 'En pausa',
  completed: 'Completado',
  cancelled: 'Cancelado',
}

export const PRIORIDAD: Record<Prioridad, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  urgent: 'Urgente',
}

export const PAPEL: Record<Papel, string> = {
  admin: 'Administración',
  developer: 'Desarrollo',
  staff: 'Equipo',
  member: 'Miembro',
  requester: 'Solicitante',
}

export const PAPEL_EXPLICADO: Record<Papel, string> = {
  admin: 'Aprueba y rechaza solicitudes, y gestiona departamentos y gente.',
  developer: 'Ve todas las solicitudes y lleva los proyectos y las tareas.',
  staff: 'Crea solicitudes y mueve tareas.',
  member: 'Crea solicitudes y ve las suyas, y lee el espacio de trabajo.',
  requester:
    'Sólo solicitudes a desarrollo: las crea y sigue las suyas (y, si dirige un departamento, ' +
    'las de su cuerpo). No ve páginas, tareas, proyectos ni equipo.',
}

/** Las columnas del tablero, en orden. `cancelled` no tiene columna: se filtra. */
export const COLUMNAS: EstadoTarea[] = [
  'backlog', 'todo', 'in_progress', 'blocked', 'review', 'done',
]

const FECHA = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
const FECHA_HORA = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
})

export function fecha(iso: string | null | undefined): string {
  if (!iso) return '—'
  return FECHA.format(new Date(iso))
}

export function fechaYHora(iso: string | null | undefined): string {
  if (!iso) return '—'
  return FECHA_HORA.format(new Date(iso))
}

/** «hace 2 horas». Con `Intl`, para no inventar plurales ni tener que traducirlos. */
const RELATIVO = new Intl.RelativeTimeFormat('es-ES', { numeric: 'auto' })
const ESCALAS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 60], ['minute', 60], ['hour', 24], ['day', 7], ['week', 4.35],
  ['month', 12], ['year', Infinity],
]

export function hace(iso: string | null | undefined): string {
  if (!iso) return '—'
  let diferencia = (new Date(iso).getTime() - Date.now()) / 1000
  for (const [unidad, tamano] of ESCALAS) {
    if (Math.abs(diferencia) < tamano) return RELATIVO.format(Math.round(diferencia), unidad)
    diferencia /= tamano
  }
  return fecha(iso)
}
